import { Injectable } from "@angular/core";
import { InAppBrowser, InAppBrowserOptions } from "@ionic-native/in-app-browser";
import { TranslateService } from "@ngx-translate/core";
import { Platform, ToastController } from "ionic-angular";
import moment from "moment";
import { Subscription } from "rxjs";
import { EventNameAnalytics } from "../../../helpers/event-name-analytics";
import { Messages } from "../../../helpers/messages";
import { BackgroundGeolocationNative } from "../../../native/background-geolocation.native";
import { BackgroundModeNative } from "../../../native/background-mode.native";
import { GeolocationNative } from "../../../native/geolocation.native";
import { AnalyticsProvider } from "../../../providers/analytics/analytics";
import { PermissionCheckProvider } from "../../../providers/permission-check/permission-check";
import { AppPreferences } from '../../../shared/local/app-preferences.local';
import { RequestPagingDto } from "../../../shared/nestjs/gd-only/dto/base.dto";
import { RequestPaginationDto } from "../../../shared/nestjs/gd-only/dto/sf-leave-request.dto";
import { RequestHistoryDetailTrackerDto, ResponseHistoryDetailTrackerDto, ResponseHistoryTrackerDto } from "../../../shared/nestjs/gd-only/dto/tracker-report.dto";
import { RequestSaveLogDto } from "../../../shared/nestjs/gd-only/dto/tracker.dto";
import { TrackerReportApi } from "../../../shared/nestjs/gd-only/services/tracker-report.api";
import { TrackerApi } from "../../../shared/nestjs/gd-only/services/tracker.api";
import { ResponsePaging } from "../../../shared/nestjs/nest-responses";
import { BackgroundGeolocationBloc } from "../background-geolocation/background-geolocation.bloc";

export interface ResponseRunTracker {
  showToastActive: boolean;
  startTrack: boolean;
  message: string;
}

@Injectable()
export class TrackerBloc {

  private subscriptions: Array<Subscription> = [];
  private _intervalTracer = null;

  constructor(
    private appPreferences: AppPreferences,
    private trackerReportApi: TrackerReportApi,
    private backgroundGeolocationBloc: BackgroundGeolocationBloc,
    private backgroundGeolocationNative: BackgroundGeolocationNative,
    private trackerApi: TrackerApi,
    private geolocationNative: GeolocationNative,
    private toastCtrl: ToastController,
    private translate: TranslateService,
    private inAppBrowser: InAppBrowser,
    private analyticsProvider: AnalyticsProvider,
    private backgroundModeNative: BackgroundModeNative,
    private permissionCheckProvider: PermissionCheckProvider,
    private platform: Platform
  ) {

  }

  dispose() {
    this.subscriptions.forEach(subs => subs.unsubscribe());
  }

  openBrowser(link) {
    const opt: InAppBrowserOptions = {
      "location": "no",
      "clearcache": "yes",
    }

    const browser = this.inAppBrowser.create(link, '_blank', opt);
    browser.show();
  }

  async runTracker(): Promise<ResponseRunTracker> {
    try {
      let result: ResponseRunTracker = {
        showToastActive: false,
        startTrack: false,
        message: ''
      }

      const SFGoAuth = await this.appPreferences.getAuth();
      if (SFGoAuth.menu['tracking']) {
        this.analyticsProvider.trackEvent(EventNameAnalytics.TRACKING_START);
        const flexibleTracker = SFGoAuth.companySetting.flexibleTracking || false;
        const isRunning = await this.backgroundGeolocationBloc.checkStatus();

        if (flexibleTracker && isRunning) {
          await this.stopTracker();
          result.startTrack = false;
        } else {
          const checkShiftTracker = await this.trackerApi.checkShiftTracker().toPromise();
          this.analyticsProvider.trackEvent(EventNameAnalytics.TRACKING_CHECK_SHIFT_TRACKER, checkShiftTracker);

          if (checkShiftTracker.data) {
            if (!isRunning) {
              this.backgroundModeNative.enable();
              await this.startTracker();
              await this.startIntervalTracker();

              result.showToastActive = true;
            } else {
              result.message = Messages.USER_CANNOT_OFF_TRACKING;
            }

            result.startTrack = true;
          } else {
            if (checkShiftTracker.message !== '') {
              result.message = checkShiftTracker.message;
            }
            await this.stopTracker();
            result.startTrack = false;
          }
        }
      } else {
        result.message = Messages.YOU_DONT_HAVE_PERMISSION;
      }

      return result;
    } catch (e) {
      this.analyticsProvider.trackEvent(EventNameAnalytics.TRACKING_START_FAILED, e);
      throw e;
    }
  }

  async reStartTracker() {
    try {
      if (this.appPreferences.dataAuth && this.appPreferences.dataAuth.menu['tracking']) {
        const checkStatusTrackingServer = await this.checkStatus();
        const checkBackgroundLocation = await this.checkStatusBackgroundLocation();
        console.log('TCL: check interval', this.intervalTracker);
        console.log('TCL: status server', checkStatusTrackingServer);
        console.log('TCL: status checkBackgroundLocation', checkBackgroundLocation);

        if (checkStatusTrackingServer) {
          if (!checkBackgroundLocation.isRunning) {
            this.runTracker();
            this.analyticsProvider.trackEvent(EventNameAnalytics.TRACKING_RESTART);
          } else if (checkBackgroundLocation.isRunning && !this.intervalTracker) {
            this.startIntervalTracker();
          }
        } else {
          if (checkBackgroundLocation.isRunning) {
            this.stopBackgroundLocation();
          }
        }

      }
    } catch (error) {
      console.log('TCL: error re start tracker', error);
    }
  }

  async checkStatus() {
    try {
      const status = await this.trackerApi.status().toPromise();
      this.analyticsProvider.trackEvent(EventNameAnalytics.TRACKING_CHECK_STATUS_TRACKER, status);

      return status.data.trackingStatus;
    } catch (error) {
      this.analyticsProvider.trackEvent(EventNameAnalytics.TRACKING_CHECK_STATUS_TRACKER_FAILED, error);
      return false;
    }
  }

  toastTrackActive() {
    return this.toastCtrl.create({
      message: this.translate.instant('Tracking has been activated'),
      position: 'bottom',
      showCloseButton: true,
      closeButtonText: this.translate.instant('Detail'),
      cssClass: 'toast-white',
      dismissOnPageChange: true
    })
  }

  private async startTracker() {
    try {
      await this.backgroundGeolocationBloc.initBackGroundLocation();
      await this.backgroundGeolocationNative.start();
    } catch (error) {
      throw error;
    }
  }

  async stopTracker() {
    try {
      const SFGoAuth = await this.appPreferences.getAuth();
      this.stopBackgroundLocation();
      const location = await this.geolocationNative.getCurrentPosition({ enableHighAccuracy: true });

      const payload = [{
        empId: SFGoAuth.empId,
        location: {
          longitude: location.coords.longitude.toString(),
          latitude: location.coords.latitude.toString(),
        },
        logTime: moment().format(),
      }];

      await this.trackerApi.end(payload).toPromise();
      this.clearIntervalSendLog();
      this.analyticsProvider.trackEvent(EventNameAnalytics.TRACKING_STOP);
    } catch (e) {
      this.analyticsProvider.trackEvent(EventNameAnalytics.TRACKING_STOP_FAILED, e);
    }
  }

  stopBackgroundLocation() {
    return this.backgroundGeolocationNative.stop();
  }

  checkStatusBackgroundLocation() {
    return this.backgroundGeolocationNative.checkStatus();
  }

  previewMasterPB(payload: RequestPaginationDto) {
    return new Promise<ResponsePaging<ResponseHistoryTrackerDto>>((resolve, reject) => {
      this.subscriptions.push(
        this.trackerReportApi.previewMasterPB(payload)
          .subscribe((result) => {
            resolve(result)
          }, error => {
            reject(error)
          })
      )
    })
  }

  previewPB(payload: RequestHistoryDetailTrackerDto) {
    return new Promise<ResponsePaging<ResponseHistoryDetailTrackerDto>>((resolve, reject) => {
      this.subscriptions.push(
        this.trackerReportApi.previewPB(payload)
          .subscribe((result) => {
            resolve(result)
          }, error => {
            reject(error)
          })
      )
    })
  }

  histories(payload: RequestPagingDto) {
    return new Promise<ResponsePaging<any>>((resolve, reject) => {
      this.subscriptions.push(
        this.trackerApi.histories(payload)
          .subscribe((result) => {
            resolve(result)
          }, error => {
            reject(error)
          })
      )
    })
  }

  /**
   * Backup background geolocation yang tidak jalan
   */
  async startIntervalTracker() {
    if (this.platform.is('cordova') && this.platform.is('ios')) {
      try {
        console.log('TCL: startIntervalTracker');
        const settings = await this.appPreferences.getSettings();
        const interval = (settings.intervalTracker) ? settings.intervalTracker * 60 * 1000 : 15 * 60 * 1000;

        // clear interval 
        if (this._intervalTracer) clearInterval(this._intervalTracer);

        // delay 1 detik untuk mastiin background mode sudah enable dan interval sudah di clear
        await new Promise(resolve => setTimeout(() => resolve(null), 1000));

        this._intervalTracer = setInterval(async () => {
          console.log('TCL: interval tracker');
          await this.saveLog();
        }, interval);

      } catch (error) {
        throw error;
      }
    }
  }

  async clearIntervalSendLog() {
    try {
      if (this._intervalTracer) {
        for (let i = 0; i <= this._intervalTracer; i++) {
          clearInterval(i);
        }
      }

      await this.backgroundModeNative.disable();
    } catch (error) {
      this.analyticsProvider.trackEvent(EventNameAnalytics.TRACKING_CLEAR_INTERVAL_FAILED, error);
    }
  }

  saveLog() {
    return new Promise(async (resolve) => {
      try {
        const payload: RequestSaveLogDto[] = [];
        const position = await this.permissionCheckProvider.getGeolocation();

        try {
          if (position.coords.latitude && position.coords.longitude) {
            const params: RequestSaveLogDto = {
              empId: this.appPreferences.dataAuth.empId,
              logTime: moment().toISOString(),
              location: {
                latitude: position.coords.latitude || 0,
                longitude: position.coords.longitude || 0
              }
            }

            payload.push(params);
            await this.trackerApi.saveLog(payload).toPromise();
            this.analyticsProvider.trackEvent(EventNameAnalytics.TRACKING_SAVE_LOG);
          } else {
            this.analyticsProvider.trackEvent(EventNameAnalytics.TRACKING_GET_LOCATION_FAILED, position);
          }
        } catch (error) {
          this.analyticsProvider.trackEvent(EventNameAnalytics.TRACKING_SAVE_LOG_FAILED, error);
        }
      } catch (error) {
        this.analyticsProvider.trackEvent(EventNameAnalytics.TRACKING_GET_LOCATION_FAILED, error);
      }

      resolve(null);
    });
  }

  get intervalTracker() {
    return this._intervalTracer;
  }

}



// WEBPACK FOOTER //
// ./src/bloc/feature/tracker/tracker.bloc.ts